import { PricingModel } from './pricing.model';
import { ServiceAddOnModel, ServiceModel } from './service.model';

/**
 * Result of the service's external availability check (e.g. the property
 * calendar in the PMS for early check-in / late check-out).
 */
export interface ExternalAvailabilityModel {
  available: boolean;
  source: string;
}

/**
 * Why a slot ended up unavailable. The backend fills a single reason per slot
 * and returns it only when the request asked for `includeInfo`.
 */
export interface SlotInfoModel {
  nonWorkingDay?: any;
  /**
   * Present when the slot falls outside the service booking window. The dates
   * are the computed boundaries (ISO), not the configured limits.
   */
  outOfbookingLimits?: {
    minTime: string;
    maxTime: string;
  };
  serviceCounter?: {
    service: ServiceModel;
    assetTypesCounter: any[];
    count: number;
    quantity: number;
  };
  serviceRequestsInSlot?: any[];
}

export interface SlotModel {
  date: {
    from: string;
    to: string[];
  };
  pricing: PricingModel;
  available: boolean;
  slotId: string;
  /** The provider sends an array; older payloads carry a single object. */
  info: SlotInfoModel | SlotInfoModel[];
  /** Only present when the service checks an external availability source. */
  externalAvailability?: ExternalAvailabilityModel;
}

export interface PostGetSlotsModel {
  service: ServiceModel;
  dateFrom: Date | string;
  dateTo: Date | string | undefined;
  quantity: number;
  includeInfo: boolean;
  pricingOptions?: {
    _id: string;
    quantity: number;
  }[];
  voucher?: string;
  apartmentId?: string;
  bookingId?: string;
}

/**
 * Availability-first flow: the guest already picked an exact slot (date + time
 * from, and date + time to for range services). `dateFrom`/`dateTo` carry the
 * full datetime of that slot.
 */
export interface PostGetAvailableOptionsModel {
  service: ServiceModel;
  dateFrom: Date | string;
  dateTo?: Date | string;
  voucher?: string;
  apartmentId?: string;
  bookingId?: string;
  /**
   * Answers of the service's preFetchDynamicFields (SurveyJS), keyed by field id.
   * Only sent for integration-priced services.
   */
  context?: Record<string, any>;
}

export interface AvailableOptionModel {
  pricingOption: {
    _id: string;
    name: string;
    price: { value: number; currency: string };
    basePrice?: { value: number; currency: string };
    description?: string;
    /** Option photos shown in the details popup. */
    images?: string[];
    /**
     * Add-ons attached to this option, resolved by the provider (integration
     * services). Same shape as a service's configured add-ons, so the add-ons
     * tab renders them without distinguishing their origin.
     */
    addOns?: ServiceAddOnModel[];
  };
  maxQuantity: number;
  available: boolean;
}

export interface AvailableOptionsResponseModel {
  available: boolean;
  slotId: string;
  /** Id of the cached provider quote (integration services). */
  quoteId?: string;
  remainingCapacity: number;
  options: AvailableOptionModel[];
  info?: SlotInfoModel[];
}
